/**
 * Доска задач, задание на смену, номенклатура и типовые действия.
 *
 * Все задачи — на одной доске: Новые → Назначены → В работе → Готово.
 * Задание на смену (дата, смена, участок, операторы, номенклатуры с количеством) создаёт задачи производства,
 * проверки оборудования участка и, где нужно, переналадки. Завершённая проверка — отчёт в «Готово»,
 * его видят вышестоящие по должности.
 */
import { z } from 'zod';
import type { ProcedureItem } from './procedures.ts';

export type TaskStatus = 'new' | 'assigned' | 'in_progress' | 'done';
export const TASK_STATUSES: readonly TaskStatus[] = ['new', 'assigned', 'in_progress', 'done'];
export const TASK_STATUS_LABEL: Record<TaskStatus, string> = { new: 'Новые', assigned: 'Назначены', in_progress: 'В работе', done: 'Готово' };

export type TaskKind = 'production' | 'check' | 'changeover' | 'other';
export const TASK_KIND_LABEL: Record<TaskKind, string> = { production: 'Производство', check: 'Проверка', changeover: 'Переналадка', other: 'Задача' };

export const SHIFT_LABEL: Record<number, string> = { 1: '1 смена', 2: '2 смена', 3: '3 смена' };

export interface TaskDto {
  id: string;
  kind: TaskKind;
  status: TaskStatus;
  title: string;
  notes: string | null;
  areaId: string | null;
  equipmentId: string | null;
  equipmentName: string | null;
  productId: string | null;
  productCode: string | null;
  productName: string | null;
  qty: number | null;
  /** Проверка: этап (before, during, after…). */
  procedureId: string | null;
  /** Отчёт проверки или переналадки. */
  checkId: string | null;
  checkStatus: 'open' | 'ok' | 'issues' | null;
  shiftDate: string | null;
  shiftNo: number | null;
  dueAt: string | null;
  assignees: { id: string; name: string }[];
  createdByName: string | null;
  createdAt: string;
  startedByName: string | null;
  startedAt: string | null;
  doneAt: string | null;
  /** Что может текущий пользователь. */
  can: { move: boolean; start: boolean; edit: boolean };
}

const Uuid = z.string().uuid();
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата: ГГГГ-ММ-ДД');

export const TaskInput = z.object({
  kind: z.enum(['production', 'check', 'changeover', 'other']).default('other'),
  title: z.string().trim().min(1, 'Что сделать?').max(200),
  notes: z.string().trim().max(3000).nullable().optional(),
  areaId: Uuid.nullable().optional(),
  equipmentId: Uuid.nullable().optional(),
  productId: Uuid.nullable().optional(),
  qty: z.number().min(0).max(1e9).nullable().optional(),
  procedureId: z.string().max(40).nullable().optional(),
  shiftDate: Day.nullable().optional(),
  shiftNo: z.number().int().min(1).max(3).nullable().optional(),
  dueAt: z.string().datetime({ offset: true }).nullable().optional(),
  assigneeIds: z.array(Uuid).max(30).optional(),
});
export type TaskInput = z.input<typeof TaskInput>;

export const TaskPatch = TaskInput.partial().extend({ status: z.enum(['new', 'assigned', 'in_progress', 'done']).optional() });
export type TaskPatch = z.input<typeof TaskPatch>;

/** Задание на смену: что делать, где и кто — из него появляются задачи на доске. */
export const ShiftInput = z.object({
  date: Day,
  shiftNo: z.number().int().min(1).max(3),
  areaId: Uuid,
  assigneeIds: z.array(Uuid).min(1, 'Кто работает в смену?').max(30),
  rows: z
    .array(
      z.object({
        productId: Uuid,
        qty: z.number().min(0).max(1e9).nullable().optional(),
        /** Машина, на которой делать (для переналадки — обязательно). */
        equipmentId: Uuid.nullable().optional(),
        changeover: z.boolean().default(false),
      }),
    )
    .max(50)
    .default([]),
  /** Добавить проверки оборудования участка (до смены, в смену, после смены). */
  checks: z.boolean().default(true),
});
export type ShiftInput = z.input<typeof ShiftInput>;

/* ---------- Номенклатура ---------- */

export interface ProductListDto {
  id: string;
  code: string;
  name: string;
  /** Всего на складе и сколько мест хранения. */
  total: number;
  places: number;
}

export interface ProductDto extends ProductListDto {
  specs: { k: string; v: string }[];
  stock: { place: string; qty: number }[];
  /** Переналадка на эту номенклатуру — пункты (ссылки на типовые действия уже раскрыты). */
  changeover: ProcedureItem[];
  /** Файлы пунктов переналадки (для показа «как сделать»). */
  files: { id: string; step: boolean; kind: 'photo' | 'video' | 'doc'; title: string; mime: string; size: number; createdAt: string; url: string }[];
  stockUpdatedAt: string | null;
}

export interface ProductImportResult {
  products: number;
  created: number;
  updated: number;
  places: number;
  /** Какие столбцы как поняли. */
  columns: { code: string; name: string; place: string | null; qty: string | null; specs: string[] };
  skipped: number;
}

/* ---------- Типовые действия ---------- */

export interface ActionDto {
  id: string;
  text: string;
  details: string | null;
  fileIds: string[];
  /** Где подключено (сколько пунктов ссылается). */
  uses: number;
  files: ProductDto['files'];
}

export const ActionInput = z.object({
  text: z.string().trim().min(1, 'Текст действия?').max(500),
  details: z.string().trim().max(3000).nullable().optional(),
  fileIds: z.array(Uuid).max(12).optional(),
});
export type ActionInput = z.input<typeof ActionInput>;
